import React from "react";
import axios from "axios";

import { Button, Modal, Form, Badge } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { NumberInput, CheckboxInput, SearchSelectInput, TextInput } from "components/global/form";
import { toast } from "react-toastify";
import CenteredLoadingSpinner from "components/owner-app/loading-spinners/CenteredLoadingSpinner";
import { generalErrorAlert } from "util/Utils";

class DoordashDriveIntegrationModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      doordashSettings: {},
      currentIntegrationOwner: "",
      loadingData: false,
      hasLoaded: false,
    };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      (this.props.show && !prevProps.show && !this.state.hasLoaded) ||
      (this.state.hasLoaded && prevProps.selectedCustomer !== this.props.selectedCustomer)
    ) {
      this.getDoordashConnectionStatus();
    }
  }

  getDoordashConnectionStatus = async () => {
    const { selectedCustomer } = this.props;
    this.setState({ loadingData: true });
    try {
      const res = await axios.get(
        "/admin/getDoordashDriveConnectionStatus?customer_id=" + selectedCustomer?.customer_id
      );
      this.setState({
        doordashSettings: res.data,
        hasLoaded: true,
        currentIntegrationOwner: res.data.delivery_group_enabled
          ? res.data.current_delivery_integration_owner.customer_name
          : "",
      });
    } catch (error) {
      generalErrorAlert(error, "Trouble retrieving DoorDash Drive settings.");
    } finally {
      this.setState({ loadingData: false });
    }
  };

  connectDoordashDrive = async (values) => {
    const { selectedCustomer, onClose, onSave, disconnectMode, allowedCustomers } = this.props;
    const { doordashSettings } = this.state;

    values.enabled = !disconnectMode;

    const payload = {
      customer_id: selectedCustomer?.customer_id,
      ...values,
    };
    try {
      await axios.post("/admin/connectDoordashDrive", payload);
      if (disconnectMode) {
        toast.success("Successfully disconnected DoorDash Drive.");
        doordashSettings.typical_prep_minutes = 25;
        doordashSettings.withhold_doordash_fees_from_payouts = true;
        doordashSettings.delivery_group_enabled = false;
        doordashSettings.current_delivery_integration_owner = "";
        doordashSettings.charge_me_for_vendor_delivery_jobs = false;
        doordashSettings.pickup_instructions = "";
        doordashSettings.should_send_notiifications = false;
        doordashSettings.uses_lof = false;
        doordashSettings.lof_business_name = "";
        this.setState({
          doordashSettings: doordashSettings,
          currentIntegrationOwner: "",
        });
      } else {
        toast.success("Successfully connected DoorDash Drive.");
        // Only set a few new doordashSetting, the form values and doordashSettings are actually very different
        const currentIntegrationOwner = values.delivery_group_enabled
          ? allowedCustomers.find((customer) => customer.customer_id === values.delivery_integration_owner_id)
          : null;
        doordashSettings.typical_prep_minutes = values.typical_prep_minutes;
        doordashSettings.withhold_doordash_fees_from_payouts = values.withhold_doordash_fees_from_payouts;
        doordashSettings.delivery_group_enabled = values.delivery_group_enabled;
        doordashSettings.current_delivery_integration_owner = currentIntegrationOwner;
        doordashSettings.charge_me_for_vendor_delivery_jobs = values.charge_me_for_vendor_delivery_jobs;
        doordashSettings.pickup_instructions = values.pickup_instructions;
        doordashSettings.should_send_notifications = values.should_send_notifications;
        doordashSettings.uses_lof = values.uses_lof;
        doordashSettings.lof_business_name = values.lof_business_name;

        this.setState({
          doordashSettings: doordashSettings,
          currentIntegrationOwner: values.delivery_group_enabled ? currentIntegrationOwner.name_for_owner : "",
        });
      }
      onSave();
      onClose();
    } catch (error) {
      generalErrorAlert(error, "Trouble connecting to DoorDash Drive.");
    }
  };

  render() {
    const { show, onClose, disconnectMode, selectedCustomer, allowedCustomers } = this.props;
    const { doordashSettings, currentIntegrationOwner, loadingData } = this.state;

    if (disconnectMode) {
      return (
        <Modal
          show={show}
          onHide={() => {
            onClose();
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>Disconnect DoorDash Drive</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className={"margin-bottom-2"}>
              <p>To disconnect DoorDash Drive, please press the "Disconnect" button below.</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              size={"sm"}
              variant={"light"}
              onClick={() => {
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              size={"sm"}
              variant={"danger"}
              onClick={() => {
                this.connectDoordashDrive(doordashSettings);
              }}
            >
              Disconnect
            </Button>
          </Modal.Footer>
        </Modal>
      );
    }

    return (
      <Modal
        show={show}
        size={"lg"}
        onHide={() => {
          onClose();
        }}
      >
        <Formik
          enableReinitialize
          initialValues={{
            typical_prep_minutes: doordashSettings.typical_prep_minutes || 25,
            withhold_doordash_fees_from_payouts: doordashSettings.withhold_doordash_fees_from_payouts || true,
            delivery_group_enabled: doordashSettings.delivery_group_enabled || false,
            delivery_integration_owner_id: doordashSettings.delivery_group_enabled
              ? doordashSettings.current_delivery_integration_owner.customer_id
              : "",
            charge_me_for_vendor_delivery_jobs: doordashSettings.charge_me_for_vendor_delivery_jobs || false,
            pickup_instructions: doordashSettings.pickup_instructions || "",
            should_send_notifications: doordashSettings.should_send_notifications || false,
            uses_lof: doordashSettings.uses_lof || false,
            lof_business_name: doordashSettings.lof_business_name || "",
          }}
          validationSchema={Yup.object({
            typical_prep_minutes: Yup.number().min(15).required("Venue ID is required."),
            withhold_doordash_fees_from_payouts: Yup.boolean(),
            delivery_group_enabled: Yup.boolean(),
            delivery_integration_owner_id: Yup.string(),
            charge_me_for_vendor_delivery_jobs: Yup.boolean(),
            pickup_instructions: Yup.string(),
            should_send_notifications: Yup.boolean(),
            uses_lof: Yup.boolean(),
            lof_business_name: Yup.string(),
          })}
          onSubmit={this.connectDoordashDrive}
        >
          {({ values, errors, touched, isValidating, handleSubmit }) => (
            <Form onSubmit={handleSubmit} className={"styled-form"}>
              <Modal.Header closeButton>
                <Modal.Title>DoorDash Drive Integration Settings</Modal.Title>
              </Modal.Header>
              <Modal.Body className={"padding-x-4"}>
                {loadingData ? (
                  <CenteredLoadingSpinner label={"Loading Doordash Drive Integration data"} />
                ) : (
                  <>
                    {!values.delivery_group_enabled && (
                      <div className={"margin-bottom-2"}>
                        <h5 className={"margin-bottom-2"}>Prerequisites for DoorDash Drive</h5>
                        {doordashSettings?.status_board?.map((prereq, index) => (
                          <div key={index}>
                            <span className={"margin-right-2"}>{prereq.description}</span>
                            <span>
                              {prereq.ok ? (
                                <Badge pill variant={"success"}>
                                  Yes
                                </Badge>
                              ) : (
                                <Badge pill variant={"danger"}>
                                  No
                                </Badge>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className={"margin-bottom-2"}>
                      <CheckboxInput
                        label={"Withhold DoorDash fees from payout? (Always yes. Send support request otherwise.)"}
                        name={"withhold_doordash_fees_from_payouts"}
                        id={"withhold-fees"}
                      />
                    </div>
                    <div className={"margin-bottom-2"}>
                      <CheckboxInput
                        label={"Should orders from this account be delivered together with orders from other accounts?"}
                        name={"delivery_group_enabled"}
                        id={"add-to-delivery-group"}
                      />
                    </div>
                    {values.delivery_group_enabled && (
                      <div className={"margin-bottom-1"}>
                        <SearchSelectInput
                          name={"delivery_integration_owner_id"}
                          id={"integration-owner"}
                          label={"Choose the main account for the food hall"}
                          options={allowedCustomers.map((customer) => {
                            return {
                              value: customer.customer_id,
                              label: customer.name_for_owner,
                            };
                          })}
                        />
                        <div className={"supporting-text margin-top-1 margin-left-2"}>
                          <p>Current Integration Owner: {" " + currentIntegrationOwner}</p>
                        </div>
                      </div>
                    )}
                    {values.delivery_group_enabled &&
                      values.delivery_integration_owner_id === selectedCustomer?.customer_id && (
                        <div className={"margin-bottom-2"}>
                          <CheckboxInput
                            label={
                              "Should delivery charges from vendors in this delivery group be charged to this account?"
                            }
                            name={"charge_me_for_vendor_delivery_jobs"}
                            id={"charge-me-for-vendor-delivery-jobs"}
                          />
                        </div>
                      )}
                    <div className={"margin-bottom-2"}>
                      <NumberInput
                        label={"Minutes before Dasher comes for pickup"}
                        name={"typical_prep_minutes"}
                        id={"typical-prep-minutes"}
                      />
                    </div>
                    <div className={"margin-bottom-2"}>
                      <TextInput
                        label={"Pickup instructions for DoorDash driver (optional)"}
                        name={"pickup_instructions"}
                        id={"pickup_instructions"}
                      />
                    </div>
                    <div className={"margin-bottom-2"}>
                      <CheckboxInput
                        label={"Should Doordash send SMS updates to guests?"}
                        name={"should_send_notifications"}
                        id={"should-send-notifications"}
                      />
                    </div>
                    <div className={"margin-bottom-2"}>
                      <CheckboxInput
                        label={"Are you using Doordash LOF (Large Order Fulfillment)?"}
                        name={"uses_lof"}
                        id={"uses-lof"}
                      />
                    </div>
                    {values.uses_lof && (
                      <>
                        <div className={"margin-bottom-1"}>
                          <TextInput
                            label={"Doordash LOF Business Name (Provided by Doordash)"}
                            name={"lof_business_name"}
                            id={"lof-business-name"}
                          />
                        </div>
                        <div className={"margin-bottom-1"}>
                          <p>
                            Ensure that you also edit the location codes that you use for delivery, to enable the
                            setting "Location is used for DoorDash LOF"
                          </p>
                        </div>
                      </>
                    )}
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  size={"sm"}
                  variant={"light"}
                  onClick={() => {
                    onClose();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size={"sm"}
                  type={"submit"}
                  disabled={
                    loadingData ||
                    Object.keys(errors).length !== 0 ||
                    (values.delivery_group_enabled && values.delivery_integration_owner_id === "") ||
                    doordashSettings?.status_board?.some((prereq) => !prereq.ok)
                  }
                >
                  Submit
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    );
  }
}

export default DoordashDriveIntegrationModal;
